import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Download, PauseCircle, Link2, AlertTriangle } from 'lucide-react'
import cd2bPropuestaService from '@/services/cd2bPropuestaService'
import { mensajeDeError } from '@/lib/errores'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import Stepper from '@/components/common/Stepper'
import useMigracion from './useMigracion'
import Bandeja from './Bandeja'
import PlanDeMigracion from './PlanDeMigracion'
import SeccionDetalle from './SeccionDetalle'
import Cuadre from './Cuadre'
import ProgresoMigracion from './ProgresoMigracion'
import Requerimientos from './Requerimientos'
import ChecklistMigracion from './ChecklistMigracion'
import ChatMigracion from './ChatMigracion'
import CopilotoPanel from './CopilotoPanel'
import TableroDatos from './TableroDatos'
import EmpezarDeCero from './EmpezarDeCero'
import { PASOS_PROYECTO, pendientesChecklist, bloqueosDeCarga } from './estados'

/**
 * A qué paso del stepper corresponde cada `estado` real del proyecto (Mongo). No es 1:1: el
 * stepper tiene 4 pasos visibles, el modelo tiene 7 estados — `revisado` y `cargando` se
 * agrupan visualmente dentro de `preparando`/`cuadrado`, que es donde el asesor sigue
 * trabajando mientras esos estados están activos.
 */
const PASO_DE_ESTADO = {
  recopilando: 'recopilando',
  preparando: 'preparando',
  revisado: 'preparando',
  cuadrado: 'cuadrado',
  cargando: 'cuadrado',
  entregado: 'entregado',
  descartado: 'recopilando',
}

/** Qué se puede dictar por chat, además de productos — mismas entidades que ya tienen perfil en el backend (`cd2bMigracionChatPerfiles.js`). */
const ENTIDADES_CHAT = [
  { clave: 'productos', etiqueta: 'Productos' },
  { clave: 'clientes', etiqueta: 'Clientes' },
  { clave: 'proveedores', etiqueta: 'Proveedores' },
  { clave: 'empleados', etiqueta: 'Empleados' },
  { clave: 'categorias', etiqueta: 'Categorías' },
  { clave: 'cuentasBancarias', etiqueta: 'Cuentas bancarias' },
  { clave: 'ubicaciones', etiqueta: 'Ubicaciones' },
]

/** Descarga un blob con nombre, sin dejar el objeto colgando en memoria. */
function descargar(blob, nombre) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = nombre
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * LA MESA DE TRABAJO — recopilar, preparar, cuadrar, cargar.
 *
 * Cuatro superficies y un solo hilo: el asesor suelta todo lo que le dieron, el sistema dice
 * qué es cada hoja, se comprueba que las hojas encajen ENTRE SÍ, y solo entonces se escribe
 * en la empresa. Ese tercer paso es el que no existía, y es el único que evita las dos
 * conversaciones que hunden una implantación: "faltan clientes" y "esto no suma lo que yo
 * tenía".
 */
export default function Proyecto({ propuestaId }) {
  const m = useMigracion(propuestaId)
  const [paso, setPaso] = useState('recopilando')
  const [pasoInicializado, setPasoInicializado] = useState(false)
  // Cómo entra el catálogo en el paso Recopilar: subiendo un archivo (de siempre) o
  // platicándolo con el agente. No es un paso nuevo del wizard — es otra forma de llenar el
  // mismo paso; una vez confirmado, el catálogo conversacional sigue el flujo normal.
  const [modoRecopilar, setModoRecopilar] = useState('archivo')
  const [confirmarEnlace, setConfirmarEnlace] = useState(false)
  const [enlacePublico, setEnlacePublico] = useState(null)

  // Lo que impide ESCRIBIR en la empresa. Se calcula aquí y se usa en el paso Cargar: un solo
  // sitio decide, en vez de tres avisos sueltos que no se enteraban unos de otros.
  const bloqueos = useMemo(
    () => bloqueosDeCarga(m.proyecto, m.especificaciones),
    [m.proyecto, m.especificaciones]
  )

  // Al abrir (o recargar) el wizard, el paso visible se toma del `estado` real del proyecto
  // en vez de arrancar siempre en Recopilar — es el fix de la causa raíz: antes, cerrar la
  // pestaña a media migración obligaba a re-navegar de memoria hasta donde se había quedado.
  useEffect(() => {
    if (pasoInicializado || !m.proyecto) return
    setPaso(PASO_DE_ESTADO[m.proyecto.estado] || 'recopilando')
    setPasoInicializado(true)
  }, [m.proyecto, pasoInicializado])

  // Los 4 pasos del stepper son también valores válidos de `estado` (`recopilando`,
  // `preparando`, `cuadrado`, `entregado`) — al navegar a mano se sincroniza con el paso
  // mismo; las transiciones automáticas (ej. tras clasificar) pueden pedir un estado más
  // preciso si aplica.
  const irA = (siguientePaso, estadoProyecto) => {
    setPaso(siguientePaso)
    m.sincronizarEstado(estadoProyecto || siguientePaso)
  }

  const pausar = async () => {
    const r = await m.pausar()
    if (!r) return
    const pendientes = pendientesChecklist(r.checklist)
    toast.success(
      pendientes.length > 0 ? `Guardado. Pendiente: ${pendientes.join(', ')}.` : 'Guardado. La checklist ya está completa.'
    )
  }

  const entidades = useMemo(
    () =>
      Object.entries(m.especificaciones || {}).map(([clave, spec]) => ({ clave, etiqueta: spec.etiqueta || clave })),
    [m.especificaciones]
  )

  // Un 404 es "no hay nada que devolver"; cualquier otra cosa es un fallo de verdad. Antes
  // todo caía en el mismo mensaje, así que un error de red se le contaba al asesor como
  // "no hay problemas" — justo lo contrario de lo que pasaba.
  const bajarCorrecciones = async () => {
    try {
      const blob = await cd2bPropuestaService.correccionesProyecto(propuestaId, m.proyecto.id, m.hojaActiva || undefined)
      descargar(blob, m.hojaActiva ? 'correcciones.xlsx' : 'correcciones-libro-completo.xlsx')
      if (!m.hojaActiva) toast('Se descargó el libro completo, no una hoja suelta.', { icon: 'ℹ️' })
    } catch (e) {
      const status = e?.response?.status
      if (status === 404) toast('No hay filas con problemas que devolver.', { icon: '✅' })
      else toast.error(mensajeDeError(e, 'No se pudo generar el archivo de correcciones'))
    }
  }

  const bajarPlantilla = async (entidad) => {
    try {
      const blob = await cd2bPropuestaService.plantillaMigracion(propuestaId, entidad)
      descargar(blob, `plantilla-${entidad}.xlsx`)
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo generar la plantilla'))
    }
  }

  const bajarPaquete = async () => {
    try {
      const r = await cd2bPropuestaService.paqueteProyecto(propuestaId, m.proyecto.id)
      if (r?.data?.url) window.open(r.data.url, '_blank', 'noopener')
    } catch {
      toast.error('No se pudo generar el paquete')
    }
  }

  // El dueño termina el wizard él mismo, sin cuenta: generar un link nuevo invalida cualquier
  // link anterior de este mismo proyecto — no hace falta "revocar" aparte, pero SÍ hace falta
  // avisarlo antes, porque si ya le mandaste uno al cliente deja de funcionarle.
  //
  // Y la URL se queda en pantalla: antes solo se copiaba al portapapeles y, si el navegador
  // negaba el permiso, el `catch` era mudo y el enlace se perdía para siempre.
  const enviarLinkAlDueno = async () => {
    setConfirmarEnlace(false)
    try {
      const r = await cd2bPropuestaService.generarEnlacePublicoProyecto(propuestaId, m.proyecto.id)
      setEnlacePublico(r.data.url)
      try {
        await navigator.clipboard.writeText(r.data.url)
        toast.success('Enlace generado y copiado al portapapeles.')
      } catch {
        toast.success('Enlace generado. Cópialo de abajo.')
      }
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo generar el enlace'))
    }
  }

  return (
    <div className="space-y-5">
      {/*
        Un fallo que deja el asistente inservible tiene que quedarse en pantalla. Medido en
        staging: una cotización sin empresa demo devolvía 400 en todo, y el asesor podía subir
        archivos y avanzar hasta un desplegable de secciones VACÍO, sin nada que se lo
        explicara — el único aviso era un toast de cuatro segundos.
      */}
      {m.impedimento && (
        <Alert variant="destructive">
          <AlertDescription className="space-y-1">
            <p className="flex items-start gap-1.5">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>{m.impedimento.detalle}</span>
            </p>
            {m.impedimento.code === 'SUB_NO_ENCONTRADA' && (
              <p>
                Esta cotización todavía no tiene <strong>empresa demo</strong>. Créala desde la cotización
                (Modalidad «Nube gestionada») y vuelve aquí: sin empresa no hay dónde escribir, y las
                secciones que se pueden migrar salen de ella.
              </p>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Stepper
        pasos={PASOS_PROYECTO}
        actual={paso}
        habilitados={PASOS_PROYECTO.map((p) => p.clave)}
        onIr={(p) => irA(p)}
      />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="min-w-0 space-y-4">
          {paso === 'recopilando' && (
            <div className="space-y-4">
              <div className="inline-flex rounded-md border border-neutral-200 dark:border-neutral-800 p-1">
                <button
                  type="button"
                  onClick={() => setModoRecopilar('archivo')}
                  className={`rounded px-3 py-1.5 text-body-sm font-medium transition-colors ${
                    modoRecopilar === 'archivo' ? 'bg-primary text-white' : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50'
                  }`}
                >
                  Subir archivo
                </button>
                <button
                  type="button"
                  onClick={() => setModoRecopilar('chat')}
                  className={`rounded px-3 py-1.5 text-body-sm font-medium transition-colors ${
                    modoRecopilar === 'chat' ? 'bg-primary text-white' : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50'
                  }`}
                >
                  Platicar con el agente
                </button>
              </div>

              {modoRecopilar === 'archivo' && (
                <TableroDatos
                  especificaciones={m.especificaciones}
                  orden={m.orden}
                  hojas={m.proyecto?.hojas || []}
                  onPlantilla={bajarPlantilla}
                />
              )}

              {modoRecopilar === 'archivo' ? (
                <Bandeja
                  proyecto={m.proyecto}
                  subiendo={m.ocupado === 'subiendo'}
                  progresoSubida={m.progresoSubida}
                  onArchivos={m.subirArchivos}
                  // Solo se avanza si de verdad se clasificó. Antes se avanzaba pase lo que
                  // pase: con la clasificación fallando, el asesor aterrizaba en «Preparar»
                  // con todas las hojas «Sin asignar» y sin ninguna pista de por qué.
                  onClasificar={async () => {
                    if (await m.clasificar()) irA('preparando', 'preparando')
                  }}
                  clasificando={m.ocupado === 'clasificando'}
                />
              ) : (
                <>
                  {/* Una vez abierto el chat la entidad queda fija — el selector solo tiene sentido ANTES de dictar el primer dato. */}
                  {!m.chat && (
                    <div className="flex flex-wrap items-center gap-2 text-body-sm">
                      <span className="text-neutral-600 dark:text-neutral-400">¿Qué vas a dictar?</span>
                      {ENTIDADES_CHAT.map((e) => (
                        <button
                          key={e.clave}
                          type="button"
                          onClick={() => m.chatAbrir(e.clave)}
                          className="rounded-full border border-neutral-300 dark:border-neutral-700 px-3 py-1 text-neutral-700 dark:text-neutral-300 transition-colors hover:border-primary-400 dark:hover:border-primary-600 hover:text-primary-700 dark:hover:text-primary-300"
                        >
                          {e.etiqueta}
                        </button>
                      ))}
                    </div>
                  )}
                  <ChatMigracion
                    proyectoId={m.proyecto?.id}
                    hojaId={m.chatHojaId}
                    entidad={m.chatEntidad}
                    chat={m.chat}
                    catalogoEnVivo={m.catalogoEnVivo}
                    onEnviarMensaje={m.chatEnviarMensaje}
                    onSubirFoto={m.chatSubirFoto}
                    onRecargarEstado={m.chatRecargarEstado}
                    onConfirmar={async () => {
                      await m.chatConfirmarCatalogo()
                      irA('preparando', 'preparando')
                    }}
                    enviando={m.chatOcupado}
                    confirmando={m.chatConfirmando}
                  />
                </>
              )}
            </div>
          )}

          {paso === 'preparando' && (
            // Plan a la izquierda, trabajo de la sección elegida + checklist a la derecha. En
            // móvil se apilan: el plan primero, porque es la vista de conjunto.
            <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
              <div className="space-y-4">
                <PlanDeMigracion
                  proyecto={m.proyecto}
                  orden={m.orden}
                  hojaActiva={m.hojaActiva}
                  onElegir={m.elegirHoja}
                />
                <ChecklistMigracion checklist={m.proyecto?.checklist} />
              </div>
              <SeccionDetalle
                hoja={m.hoja}
                muestra={m.muestra}
                especificaciones={m.especificaciones}
                entidades={entidades}
                onCambiarEntidad={(entidad) => m.ajustarHoja(m.hojaActiva, { entidad })}
                onCambiarMapeo={(mapeo) => m.ajustarHoja(m.hojaActiva, { mapeo })}
                onEnsayar={() => m.ensayar(m.hojaActiva)}
                ensayando={m.ocupado === 'ensayando'}
                onSugerirIA={() => m.sugerirIA(m.hojaActiva)}
                onDecidirSugerencia={(aceptar) => m.decidirSugerencia(m.hojaActiva, aceptar)}
                sugiriendo={m.ocupado === 'sugiriendo'}
                perfiles={m.perfiles}
                onAplicarPerfil={m.aplicarPerfil}
                onGuardarPerfil={m.guardarPerfil}
              />
            </div>
          )}

          {paso === 'cuadrado' && (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,18rem)]">
              <div className="space-y-4">
                <Cuadre
                  cuadre={m.proyecto?.cuadre}
                  cargando={m.ocupado === 'cuadrando'}
                  onCuadrar={m.cuadrar}
                  onDescargarCorrecciones={bajarCorrecciones}
                />
                <Requerimientos
                  informe={m.hallazgos}
                  cargando={m.ocupado === 'detectando'}
                  onDetectar={() => m.detectarHallazgos({})}
                  onDecidir={m.decidirHallazgo}
                />
              </div>
              <ChecklistMigracion checklist={m.proyecto?.checklist} />
            </div>
          )}

          {paso === 'entregado' && (
            <div className="space-y-5">
              {m.empresaDemo ? (
                <>
                  <ProgresoMigracion
                    progreso={m.progreso}
                    hojas={m.proyecto?.hojas || []}
                    onCargar={() => m.cargar()}
                    // Reanudar retoma la tarjeta que se cortó: el api sabe cuál es.
                    onReanudar={() => m.cargar({ forzar: true })}
                    cargando={m.ocupado === 'cargando'}
                    bloqueos={bloqueos}
                    onIrAHoja={(hojaId) => {
                      irA('preparando')
                      if (hojaId) m.elegirHoja(hojaId)
                    }}
                    etiquetaDe={(e) => m.especificaciones?.[e]?.etiqueta || e}
                  />
                  <TableroDatos
                    especificaciones={m.especificaciones}
                    orden={m.orden}
                    hojas={m.proyecto?.hojas || []}
                    cargas={m.proyecto?.cargas || []}
                    progreso={m.progreso}
                    onPlantilla={bajarPlantilla}
                    onCargar={(entidad) => m.cargar({ entidad })}
                    onDeshacer={m.deshacerCarga}
                    bloqueosDe={(entidad) => bloqueosDeCarga(m.proyecto, m.especificaciones, entidad)}
                  />
                  <EmpezarDeCero
                    enMarcha={m.progreso?.fase === 'cargando' || m.progreso?.fase === 'cerrando'}
                    hayQueCargar={(m.proyecto?.hojas || []).some((h) => h.entidad && h.estado !== 'omitida')}
                    onPrevisualizar={() => m.vaciarEmpresa({ previsualizar: true })}
                    onVaciar={({ recargar }) => m.vaciarEmpresa({ recargar })}
                  />
                </>
              ) : (
                <>
                  {/* Entregada, la operación es real: importar encima pisaría precios y clientes, y
                      deshacer borraría lo que el cliente ya usó. Queda a la vista lo que entró. */}
                  <Alert variant="warning">
                    <AlertDescription>
                      La empresa ya está en producción. Desde aquí ya no se carga, deshace ni vacía: lo que se importó
                      queda como está.
                    </AlertDescription>
                  </Alert>
                  <TableroDatos
                    especificaciones={m.especificaciones}
                    orden={m.orden}
                    hojas={m.proyecto?.hojas || []}
                    cargas={m.proyecto?.cargas || []}
                    progreso={m.progreso}
                    onPlantilla={bajarPlantilla}
                  />
                </>
              )}
            </div>
          )}
        </div>

        {/* El copiloto acompaña los 4 pasos — no desaparece al salir de Recopilar, a
            diferencia de ChatMigracion (que solo arma el catálogo inicial). */}
        {m.proyecto && (
          <div className="xl:sticky xl:top-4 xl:self-start">
            <CopilotoPanel
              mensajes={m.copiloto}
              propuestaPendiente={m.copilotoPropuestaPendiente}
              onEnviarMensaje={m.copilotoEnviarMensaje}
              onLimpiar={m.copilotoLimpiar}
              ocupado={m.copilotoOcupado}
              paso={paso}
              hojaActivaId={m.hojaActiva}
            />
          </div>
        )}
      </div>

      {m.proyecto && (
        <div className="flex flex-wrap items-center gap-2 border-t border-neutral-200 dark:border-neutral-800 pt-4">
          <Button variant="ghost" size="sm" onClick={pausar}>
            <PauseCircle size={15} className="mr-1.5" /> Guardar y continuar después
          </Button>
          {/* El paquete se puede pedir en cualquier momento: es un archivo, no un cierre. */}
          <Button variant="ghost" size="sm" onClick={bajarPaquete}>
            <Download size={15} className="mr-1.5" /> Guardar la migración (.cd2bmig)
          </Button>
          {/* El dueño puede terminar esto él mismo, sin cuenta ni sesión — pensado para cuando
              el asesor prefiere delegarle el trabajo de mapear al propio cliente. */}
          <Button variant="ghost" size="sm" onClick={() => setConfirmarEnlace(true)}>
            <Link2 size={15} className="mr-1.5" /> Enviar link al dueño
          </Button>
          {m.proyecto.pausadoEn && (
            <span className="text-caption text-neutral-400 dark:text-neutral-500">
              Pausado el {new Date(m.proyecto.pausadoEn).toLocaleDateString('es-SV')}
            </span>
          )}
        </div>
      )}

      {enlacePublico && (
        <div className="space-y-1">
          <p className="text-body-sm font-medium text-neutral-900 dark:text-neutral-50">
            Enlace para el dueño (no se vuelve a mostrar):
          </p>
          <Input readOnly value={enlacePublico} onFocus={(e) => e.target.select()} />
        </div>
      )}

      <ConfirmDialog
        abierto={confirmarEnlace}
        onCerrar={() => setConfirmarEnlace(false)}
        onConfirmar={enviarLinkAlDueno}
        titulo="¿Generar un enlace nuevo para el dueño?"
        descripcion="Si ya le mandaste uno, dejará de funcionar en cuanto generes este. El dueño podrá terminar la migración sin cuenta ni contraseña."
        textoConfirmar="Generar el enlace"
      />
    </div>
  )
}
